import type { ChangeEvent, DragEvent } from 'react';
import type { UploadthingReactClient } from '../client/client';
import type { UploadedFile } from '../types';
import { CheckCircle2, FileVideo, Upload, X } from 'lucide-react';
import { useCallback, useRef, useState } from 'react';
import {
  filterVerticalVideos,
  formatFileSize,
  useVideoCompression,
} from '../client';
import css from './compressed-video-uploader.module.scss';

export type CompressionOption = 10 | 15 | 20 | null;

type Props = {
  // The package-bound `useUploadThing` hook from the consumer's
  // `createUploadthingClient(...)` instance. Passed in so the package
  // stays decoupled from the consumer's auth-token resolver.
  useUploadThing: UploadthingReactClient['useUploadThing'];
  onUploadComplete: (files: UploadedFile[]) => void;
  onUploadError?: (error: Error) => void;
  onUploadBegin?: () => void;
  // When true, after file selection the user picks a target compressed
  // size (10/15/20 MB or "No compression"). When false, compresses with
  // default settings if the file exceeds the threshold.
  compressionOptions?: boolean;
  className?: string;
};

type Stage =
  | 'idle'
  | 'selecting'
  | 'compressing'
  | 'uploading'
  | 'complete'
  | 'error';

type Stats = {
  originalSize: number;
  compressedSize: number;
  wasCompressed: boolean;
};

export const CompressedVideoUploader = ({
  useUploadThing,
  onUploadComplete,
  onUploadError,
  onUploadBegin,
  compressionOptions = false,
  className,
}: Props) => {
  const [stage, setStage] = useState<Stage>('idle');
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [compressionStats, setCompressionStats] = useState<Stats | null>(null);

  const fileInputRef = useRef<HTMLInputElement>(null);

  const { compressVideo, progress: compressionProgress } =
    useVideoCompression();

  const { startUpload } = useUploadThing('videoUploader', {
    onClientUploadComplete: (res) => {
      setStage('complete');
      if (res && res.length > 0) {
        onUploadComplete(
          res.map((file) => ({
            key: file.key,
            name: file.name,
            url: file.ufsUrl,
          }))
        );
      }
    },
    onUploadError: (error) => {
      setStage('error');
      setErrorMessage(error.message);
      onUploadError?.(error);
    },
    onUploadProgress: (p) => setUploadProgress(p),
    onUploadBegin: () => onUploadBegin?.(),
  });

  const processAndUpload = useCallback(
    async (file: File, targetSizeMB?: number) => {
      setErrorMessage(null);
      setCompressionStats(null);

      setStage('compressing');
      const result = await compressVideo(file, targetSizeMB);

      if (result.error) {
        setStage('error');
        setErrorMessage(result.error);
        return;
      }

      setCompressionStats({
        originalSize: result.originalSize,
        compressedSize: result.compressedSize,
        wasCompressed: result.wasCompressed,
      });

      setStage('uploading');
      setUploadProgress(0);

      try {
        await startUpload([result.file]);
      } catch (error) {
        setStage('error');
        setErrorMessage(
          error instanceof Error ? error.message : 'Upload failed'
        );
      }
    },
    [compressVideo, startUpload]
  );

  const handleFileSelect = useCallback(
    async (file: File) => {
      try {
        const { accepted, skippedCount } = await filterVerticalVideos([file]);
        if (accepted.length === 0 && skippedCount > 0) {
          setStage('error');
          setErrorMessage(
            'Vertical videos are not supported. Please upload a horizontal (landscape) video.'
          );
          return;
        }
      } catch {
        // Unreadable/corrupt video — fail closed with the same guidance so the
        // upload never silently hangs (legacy parity).
        setStage('error');
        setErrorMessage(
          'Vertical videos are not supported. Please upload a horizontal (landscape) video.'
        );
        return;
      }

      setSelectedFile(file);
      setErrorMessage(null);
      setCompressionStats(null);

      if (compressionOptions) {
        setStage('selecting');
        return;
      }

      await processAndUpload(file);
    },
    [compressionOptions, processAndUpload]
  );

  const handleCompressionChoice = useCallback(
    (option: CompressionOption) => {
      if (!selectedFile) return;
      processAndUpload(selectedFile, option ?? undefined);
    },
    [selectedFile, processAndUpload]
  );

  const handleInputChange = (event: ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (file) handleFileSelect(file);
    event.target.value = '';
  };

  const handleDrop = (event: DragEvent<HTMLButtonElement>) => {
    event.preventDefault();
    event.stopPropagation();
    const file = event.dataTransfer.files?.[0];
    if (file?.type.startsWith('video/')) {
      handleFileSelect(file);
    }
  };

  const handleDragOver = (event: DragEvent<HTMLButtonElement>) => {
    event.preventDefault();
    event.stopPropagation();
  };

  const handleReset = () => {
    setStage('idle');
    setSelectedFile(null);
    setUploadProgress(0);
    setErrorMessage(null);
    setCompressionStats(null);
  };

  const openFileDialog = () => fileInputRef.current?.click();

  return (
    <div className={className ? `${css.root} ${className}` : css.root}>
      <input
        accept="video/*"
        onChange={handleInputChange}
        ref={fileInputRef}
        style={{ display: 'none' }}
        type="file"
      />

      {stage === 'idle' && (
        <button
          className={css.dropzone}
          onClick={openFileDialog}
          onDragOver={handleDragOver}
          onDrop={handleDrop}
          type="button"
        >
          <Upload className={css.dropzoneIcon} size={32} />
          <span className={css.dropzonePrompt}>
            Click to upload or drag and drop
          </span>
          <span className={css.dropzoneHint}>
            MP4, WebM, MOV up to 256 MB. Videos over 10 MB are compressed
            automatically.
          </span>
        </button>
      )}

      {stage === 'selecting' && selectedFile && (
        <div className={css.selecting}>
          <FileVideo size={32} />
          <span className={`${css.text} ${css.textMedium}`}>
            {selectedFile.name}
          </span>
          <span className={css.selectingMeta}>
            Size: {formatFileSize(selectedFile.size)}
          </span>

          <span className={css.text}>Choose compression target</span>
          <div className={css.compressionGrid}>
            {([10, 15, 20] as const).map((sizeMB) => {
              const targetBytes = sizeMB * 1024 * 1024;
              const isDisabled = selectedFile.size <= targetBytes;
              return (
                <button
                  className={css.btn}
                  disabled={isDisabled}
                  key={sizeMB}
                  onClick={() => handleCompressionChoice(sizeMB)}
                  type="button"
                >
                  {sizeMB} MB
                </button>
              );
            })}
            <button
              className={css.btn}
              onClick={() => handleCompressionChoice(null)}
              type="button"
            >
              No compression
            </button>
          </div>

          <button
            className={`${css.btn} ${css.btnGhost} ${css.btnSm}`}
            onClick={handleReset}
            type="button"
          >
            Cancel
          </button>
        </div>
      )}

      {stage === 'compressing' && (
        <div className={css.busy}>
          <span aria-label="Loading" className={css.spinner} role="status" />
          <span className={`${css.text} ${css.textMedium}`}>
            {compressionProgress.message || 'Compressing video...'}
          </span>
          {compressionProgress.progress > 0 && (
            <div className={css.progressBar}>
              <div
                className={css.progressFill}
                style={{ width: `${compressionProgress.progress}%` }}
              />
            </div>
          )}
          {selectedFile && (
            <span className={css.selectingMeta}>
              {selectedFile.name} ({formatFileSize(selectedFile.size)})
            </span>
          )}
        </div>
      )}

      {stage === 'uploading' && (
        <div className={css.busy}>
          <span aria-label="Loading" className={css.spinner} role="status" />
          <span className={`${css.text} ${css.textMedium}`}>
            Uploading video...
          </span>
          <div className={css.progressBar}>
            <div
              className={`${css.progressFill} ${css.progressFillSuccess}`}
              style={{ width: `${uploadProgress}%` }}
            />
          </div>
          <span className={css.selectingMeta}>{uploadProgress}%</span>
          {compressionStats && (
            <div className={css.stats}>
              {compressionStats.wasCompressed ? (
                <>
                  Compressed: {formatFileSize(compressionStats.originalSize)} →{' '}
                  {formatFileSize(compressionStats.compressedSize)}
                  <br />
                  <span className={css.statsSaved}>
                    Saved{' '}
                    {Math.round(
                      ((compressionStats.originalSize -
                        compressionStats.compressedSize) /
                        compressionStats.originalSize) *
                        100
                    )}
                    %
                  </span>
                </>
              ) : (
                <>
                  Size: {formatFileSize(compressionStats.originalSize)} (no
                  compression needed)
                </>
              )}
            </div>
          )}
        </div>
      )}

      {stage === 'complete' && (
        <div className={css.complete}>
          <CheckCircle2 size={28} />
          <span className={`${css.text} ${css.textMedium}`}>
            Upload complete!
          </span>
          {compressionStats?.wasCompressed && (
            <span className={css.selectingMeta}>
              Compressed from {formatFileSize(compressionStats.originalSize)} to{' '}
              {formatFileSize(compressionStats.compressedSize)}
            </span>
          )}
          <button
            className={`${css.btn} ${css.btnGhost} ${css.btnSm}`}
            onClick={handleReset}
            type="button"
          >
            Upload another video
          </button>
        </div>
      )}

      {stage === 'error' && (
        <div className={css.error}>
          <X size={28} />
          <span className={`${css.text} ${css.textMedium}`}>Upload failed</span>
          {errorMessage && <span className={css.text}>{errorMessage}</span>}
          <button
            className={`${css.btn} ${css.btnSm}`}
            onClick={handleReset}
            type="button"
          >
            Try again
          </button>
        </div>
      )}
    </div>
  );
};
